<script setup lang="ts">
import { liveClient, useConnectionStore, useNotificationsStore, useUser, type NavItem } from '@itc/sdk';
import { AppShell, Banner, ConnectionBanner, Icon, ToastHost } from '@itc/ui';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import { firstAdminPath } from './admin/sections.ts';
import InboxButton from './components/InboxButton.vue';
import NavBadge from './components/NavBadge.vue';
import SearchSheet from './components/SearchSheet.vue';
import UserMenu from './components/UserMenu.vue';
import { moduleNav } from './modules.ts';
import { trialBanner, trialOf } from './trial.ts';

const route = useRoute();
const router = useRouter();
const user = useUser();
const connection = useConnectionStore();
const notifications = useNotificationsStore();

// The live stream runs while someone is logged in (design §6.5), and brings their notifications.
const live = liveClient();
connection.bind(live);
notifications.bind(live);
// The session ended under us (a ban, a revoked session, lost access): to the login page.
live.onUnauthorized(() => {
  void user.load().then(() => router.push({ path: '/login', query: { next: route.fullPath } }));
});
watch(
  () => user.isLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      live.start();
      void notifications.load();
    } else {
      live.stop();
      notifications.reset();
    }
  },
  { immediate: true },
);

const toasts = computed(() => notifications.toasts.map(({ id, item }) => ({ id, title: item.title, ...(item.body === '' ? {} : { body: item.body }) })));

async function openToast(id: string): Promise<void> {
  const item = notifications.toasts.find((entry) => entry.id === id)?.item;
  notifications.dismiss(id);
  if (item === undefined) return;
  if (item.readAt === null) await notifications.markRead(id);
  if (item.link !== null) await router.push(item.link);
}

const homeNav: NavItem = { label: 'Home', to: '/', permission: 'authenticated', icon: 'home' };
const adminNav: NavItem = { label: 'Admin', to: '/admin', permission: 'authenticated', icon: 'admin' };
const nav = computed(() => [
  ...[homeNav, ...moduleNav].filter((item) => user.can(item.permission)),
  // Anyone who may open at least one admin section.
  ...(firstAdminPath((permission) => user.can(permission)) === null ? [] : [adminNav]),
]);

// A module still in its trial says so on each of its pages (cutover milestone A).
const trial = computed(() => trialOf(route.path, user.trials));

// Search: `/` opens it, but not while typing in a field or with a sheet open (line 1151).
const searchOpen = ref(false);
function onKey(event: KeyboardEvent): void {
  if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || !user.isLoggedIn) return;
  const target = event.target as HTMLElement | null;
  if (target?.closest('input, textarea, select, [contenteditable="true"]') != null || document.querySelector('[role="dialog"]') !== null) return;
  event.preventDefault();
  searchOpen.value = true;
}
onMounted(() => {
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
});

// The rail's connection status (Sierra's .sync): live, reconnecting, or live without Discord.
const status = computed(() => {
  if (connection.discordUnavailable) return { tone: 'crit', label: 'Discord unavailable' };
  if (connection.state === 'open') return { tone: 'good', label: 'Live' };
  if (connection.state === 'reconnecting') return { tone: 'warn', label: 'Reconnecting' };
  return { tone: 'neutral', label: 'Connecting' };
});
</script>

<template>
  <RouterView v-if="route.meta.layout === 'bare'" />
  <AppShell v-else app-name="ITC Web" app-sub="Org console" :data-live-state="connection.state">
    <template #nav>
      <RouterLink v-for="item in nav" :key="item.to" :to="item.to" class="itc-rail__item" active-class="itc-rail__item--active">
        <Icon :name="item.icon" />
        <span>{{ item.label }}</span>
        <NavBadge v-if="item.badge && user.can(item.badge.permission)" :badge="item.badge" />
      </RouterLink>
      <button v-if="user.user" type="button" class="itc-rail__item shell-search" data-testid="open-search" @click="searchOpen = true">
        <Icon name="search" />
        <span>Search</span>
      </button>
    </template>

    <template #foot>
      <template v-if="user.user">
        <InboxButton />
        <UserMenu />
        <div class="itc-status shell-status" :data-tone="status.tone" :title="`Connection: ${status.label}`">{{ status.label }}</div>
      </template>
    </template>

    <ConnectionBanner :reconnecting="connection.reconnecting" :discord-unavailable="connection.discordUnavailable" />
    <Banner v-if="trial" tone="warn" class="shell-trial" data-testid="trial-banner">{{ trialBanner(trial.title) }}</Banner>
    <RouterView />
    <ToastHost :toasts="toasts" @dismiss="notifications.dismiss($event)" @open="openToast" />
    <SearchSheet :open="searchOpen" @close="searchOpen = false" />
  </AppShell>
</template>

<style scoped>
.shell-trial {
  margin-bottom: var(--itc-s4);
}
.shell-search {
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;
}
@media (max-width: 720px) {
  /* The phone header shows the dot only; the banner says more when it matters. */
  .shell-status {
    justify-content: flex-start;
    width: 19px;
    padding: 6px;
    overflow: hidden;
    color: transparent;
  }
}
</style>
